// 시작 전 GPU 실사용 점검과 백엔드 선택. 실제 게임 캔버스를 잡기 전에 임시 캔버스로 WebGPU 전 경로를 시험한다.
// 배경(2026-10-15 실측): three의 자동 대체는 WebGPU 초기화가 실패하면 WebGL2 컨텍스트가 null인지 확인하지 않고
// getSupportedExtensions를 불러 원시 예외로 멈춘다. 또 WebGPU 어댑터·디바이스가 있어도 캔버스 출력에서 디바이스가
// 'destroyed'로 끝나는 환경이 있는데, three는 이 사유를 onDeviceLost로 넘기지 않아 로딩 화면에서 멈춘다.
// 그래서 어댑터 유무만 보지 않고 필요한 한도·버퍼·파이프라인·캔버스 출력까지 한 번 시험해 본 뒤 고른다.

/** WebGPU 버퍼 사용 플래그(GPUBufferUsage 값. 테스트 환경에는 전역이 없어 숫자로 둔다). */
const BUF = { COPY_SRC: 0x04, COPY_DST: 0x08, INDEX: 0x10, VERTEX: 0x20, UNIFORM: 0x40 } as const;
const TEX_RENDER_ATTACHMENT = 0x10;
const STAGE_VERTEX = 0x1;

/**
 * 게임이 실제로 쓰는 자원 기준(2026-10-15 GPUDevice 계측: 정점 버퍼 최대 3.69MB, 인덱스 0.48MB, 유니폼 37,376B,
 * 파이프라인당 정점 버퍼 최대 6개, gltf 정적 속성 묶음 상한 8개, 그림자 맵 최대 2048).
 * 호환(compatibility) 디바이스 기본 유니폼 한도는 16,384B라 그대로 두면 37KB 유니폼에서 깨진다. 그래서 한도를 명시해 요청한다.
 */
export const WEBGPU_NEEDS = {
  limits: {
    maxUniformBufferBindingSize: 65536,
    maxVertexBuffers: 8,
    maxTextureDimension2D: 2048,
    maxBufferSize: 16 * 1024 * 1024,
  },
  /** 시험 버퍼 크기(실측 최대치보다 조금 크게) */
  buffers: { vertex: 4 * 1024 * 1024, index: 1024 * 1024, uniform: 65536 },
  vertexBuffers: 8,
} as const;

export type WebGpuStage = 'no-api' | 'no-adapter' | 'limits' | 'device' | 'buffers' | 'pipeline' | 'present' | 'lost' | 'validation' | 'timeout';

export interface WebGpuProbeResult {
  ok: boolean;
  /** 실패한 단계 */
  stage?: WebGpuStage;
  detail?: string;
  /** 성공 시 렌더러가 디바이스를 만들 때 요청할 한도 */
  requiredLimits?: Record<string, number>;
  ms: number;
}

export interface WebGl2ProbeResult {
  ok: boolean;
  detail?: string;
  /** WebGL2를 만들어 본 횟수(재시도 포함) */
  attempts?: number;
}

/** 브라우저 객체 대신 넣을 수 있는 최소 표면(단위 테스트용). */
export interface ProbeCanvas {
  getContext(type: string, attrs?: unknown): unknown;
}
export interface WebGpuProbeEnv {
  gpu: unknown;
  makeCanvas: () => ProbeCanvas;
  /** 전체 시험 제한 시간(ms) */
  timeoutMs?: number;
  /** 출력 뒤 디바이스 유실을 기다리는 시간(ms) */
  settleMs?: number;
}

type AnyObj = Record<string, any>;
const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
const msg = (e: unknown): string => (e instanceof Error ? e.name + ': ' + e.message : String(e));
const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

class ProbeFail extends Error {
  constructor(readonly stage: WebGpuStage, detail: string) { super(detail); }
}

const SHADER = (() => {
  const n = WEBGPU_NEEDS.vertexBuffers;
  const fields = Array.from({ length: n }, (_, i) => '@location(' + i + ') a' + i + ': vec4f').join(', ');
  const sum = Array.from({ length: n }, (_, i) => 'i.a' + i).join(' + ');
  const vec4s = WEBGPU_NEEDS.buffers.uniform / 16;
  return 'struct U { v: array<vec4f, ' + vec4s + '> };\n' +
    '@group(0) @binding(0) var<uniform> u: U;\n' +
    'struct VI { ' + fields + ' };\n' +
    '@vertex fn vs(i: VI, @builtin(vertex_index) vi: u32) -> @builtin(position) vec4f {\n' +
    '  return vec4f((' + sum + ').xyz * 1e-9, 1.0) + u.v[vi % ' + vec4s + 'u] * 1e-9;\n}\n' +
    '@fragment fn fs() -> @location(0) vec4f { return vec4f(0.0, 0.0, 0.0, 1.0); }\n';
})();

/**
 * WebGPU 실사용 시험: 어댑터 → 한도 → 디바이스(필요 한도 요청) → 실측 크기 버퍼(mappedAtCreation) →
 * 정점 버퍼 8개·유니폼 파이프라인 → 임시 캔버스 출력 → 제출 완료·유실·검증 오류 확인. 시험 디바이스는 끝나면 파괴한다.
 */
export async function probeWebGpu(env: WebGpuProbeEnv): Promise<WebGpuProbeResult> {
  const t0 = now();
  const gpu = env.gpu as AnyObj | undefined | null;
  if (!gpu || typeof gpu.requestAdapter !== 'function') return { ok: false, stage: 'no-api', detail: 'navigator.gpu 없음', ms: 0 };
  // 정리용 핸들(비동기 시험 함수 안에서 채운다)
  const held: { device: AnyObj | null; context: AnyObj | null } = { device: null, context: null };
  let lost: string | null = null;
  let finished = false;
  // 시간 초과로 결과를 이미 돌려준 뒤에는 늦게 도착한 자원을 바로 정리하고 더 만들지 않는다
  const bail = (): void => { if (finished) throw new ProbeFail('timeout', '시간 초과 뒤 중단'); };
  const run = async (): Promise<WebGpuProbeResult> => {
    let adapter: AnyObj | null;
    try { adapter = await gpu.requestAdapter({ featureLevel: 'compatibility' }); } catch (e) { throw new ProbeFail('no-adapter', msg(e)); }
    bail();
    if (!adapter) throw new ProbeFail('no-adapter', 'requestAdapter가 null을 돌려줌');
    const limits = (adapter.limits ?? {}) as AnyObj;
    const short = Object.entries(WEBGPU_NEEDS.limits).filter(([k, v]) => !(Number(limits[k]) >= v));
    if (short.length) throw new ProbeFail('limits', short.map(([k, v]) => k + ' ' + limits[k] + ' < ' + v).join(', '));
    const requiredLimits: Record<string, number> = { ...WEBGPU_NEEDS.limits };
    let device: AnyObj | null = null;
    try { device = await adapter.requestDevice({ requiredLimits }); } catch (e) { throw new ProbeFail('device', msg(e)); }
    if (finished) { try { device?.destroy?.(); } catch { /* 무시 */ } bail(); }
    held.device = device;
    if (!device) throw new ProbeFail('device', 'requestDevice가 디바이스를 돌려주지 않음');
    const dev = device;
    const lostPromise: Promise<void> = Promise.resolve(dev.lost).then((info: AnyObj | undefined) => {
      if (!finished) lost = String(info?.reason ?? 'unknown') + ': ' + String(info?.message ?? '');
    });
    for (const f of ['validation', 'out-of-memory', 'internal']) try { dev.pushErrorScope(f); } catch { /* 일부 구현은 internal 미지원 */ }

    // 1) 실제 크기 버퍼(three는 정점·인덱스를 mappedAtCreation으로 올린다)
    let vb: AnyObj, ib: AnyObj, ub: AnyObj;
    try {
      vb = dev.createBuffer({ size: WEBGPU_NEEDS.buffers.vertex, usage: BUF.VERTEX | BUF.COPY_DST | BUF.COPY_SRC, mappedAtCreation: true });
      new Uint8Array(vb.getMappedRange()).fill(0); vb.unmap();
      ib = dev.createBuffer({ size: WEBGPU_NEEDS.buffers.index, usage: BUF.INDEX | BUF.COPY_DST | BUF.COPY_SRC, mappedAtCreation: true });
      new Uint8Array(ib.getMappedRange()).fill(0); ib.unmap();
      ub = dev.createBuffer({ size: WEBGPU_NEEDS.buffers.uniform, usage: BUF.UNIFORM | BUF.COPY_DST });
      dev.queue.writeBuffer(ub, 0, new Uint8Array(256));
    } catch (e) { throw new ProbeFail('buffers', msg(e)); }

    // 2) 정점 버퍼 8개 + 큰 유니폼을 쓰는 파이프라인(캔버스 선호 포맷)
    const format: string = typeof gpu.getPreferredCanvasFormat === 'function' ? gpu.getPreferredCanvasFormat() : 'bgra8unorm';
    let pipeline: AnyObj, bindGroup: AnyObj;
    try {
      const module = dev.createShaderModule({ code: SHADER });
      const bgl = dev.createBindGroupLayout({ entries: [{ binding: 0, visibility: STAGE_VERTEX, buffer: { type: 'uniform', minBindingSize: WEBGPU_NEEDS.buffers.uniform } }] });
      const layout = dev.createPipelineLayout({ bindGroupLayouts: [bgl] });
      const buffers = Array.from({ length: WEBGPU_NEEDS.vertexBuffers }, (_, i) => ({ arrayStride: 16, attributes: [{ shaderLocation: i, offset: 0, format: 'float32x4' }] }));
      pipeline = await dev.createRenderPipelineAsync({ layout, vertex: { module, entryPoint: 'vs', buffers }, fragment: { module, entryPoint: 'fs', targets: [{ format }] }, primitive: { topology: 'triangle-list' } });
      bail();
      bindGroup = dev.createBindGroup({ layout: bgl, entries: [{ binding: 0, resource: { buffer: ub } }] });
    } catch (e) { throw e instanceof ProbeFail ? e : new ProbeFail('pipeline', msg(e)); }

    // 3) 임시 캔버스로 화면 출력(스왑 체인) — 실제 게임 캔버스는 건드리지 않는다
    try {
      const canvas = env.makeCanvas();
      const context = canvas.getContext('webgpu') as AnyObj | null;
      held.context = context;
      if (!context) throw new Error('webgpu 캔버스 컨텍스트 없음');
      context.configure({ device: dev, format, alphaMode: 'premultiplied' });
      const tex = context.getCurrentTexture();
      const enc = dev.createCommandEncoder();
      const pass = enc.beginRenderPass({ colorAttachments: [{ view: tex.createView(), loadOp: 'clear', storeOp: 'store', clearValue: { r: 0, g: 0, b: 0, a: 1 } }] });
      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bindGroup);
      for (let i = 0; i < WEBGPU_NEEDS.vertexBuffers; i++) pass.setVertexBuffer(i, vb);
      pass.setIndexBuffer(ib, 'uint16');
      pass.drawIndexed(3);
      pass.end();
      dev.queue.submit([enc.finish()]);
    } catch (e) { throw new ProbeFail('present', msg(e)); }
    await Promise.race([Promise.resolve(dev.queue.onSubmittedWorkDone()).catch(() => undefined), lostPromise]);
    // 출력 실패는 제출 뒤 수십 ms 안에 디바이스 유실로 드러난다(실측 20~40ms)
    await Promise.race([lostPromise, delay(env.settleMs ?? 120)]);
    if (lost) throw new ProbeFail('lost', lost);
    const scoped: string[] = [];
    for (let i = 0; i < 3; i++) {
      try { const e = await dev.popErrorScope(); if (e) scoped.push(String((e as AnyObj).message ?? e)); } catch (e) { if (i === 0) throw new ProbeFail('lost', msg(e)); }
    }
    if (lost) throw new ProbeFail('lost', lost);
    if (scoped.length) throw new ProbeFail('validation', scoped.join(' | '));
    return { ok: true, requiredLimits, ms: Math.round(now() - t0) };
  };
  const timeoutMs = env.timeoutMs ?? 5000;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      run(),
      new Promise<WebGpuProbeResult>((_, reject) => { timer = setTimeout(() => reject(new ProbeFail('timeout', timeoutMs + 'ms 안에 끝나지 않음')), timeoutMs); }),
    ]);
  } catch (e) {
    const f = e instanceof ProbeFail ? e : new ProbeFail('validation', msg(e));
    return { ok: false, stage: f.stage, detail: f.message, ms: Math.round(now() - t0) };
  } finally {
    finished = true;
    if (timer !== undefined) clearTimeout(timer);
    try { held.context?.unconfigure?.(); } catch { /* 무시 */ }
    try { held.device?.destroy?.(); } catch { /* 무시 */ }
  }
}

/** WebGL2를 임시 캔버스에서 실제로 만들어 본다(null 컨텍스트·확장 목록 null을 실패로 본다). 끝나면 컨텍스트를 놓는다. */
export function probeWebGl2(makeCanvas: () => ProbeCanvas): WebGl2ProbeResult {
  try {
    const gl = makeCanvas().getContext('webgl2', { failIfMajorPerformanceCaveat: false }) as AnyObj | null;
    if (!gl) return { ok: false, detail: 'getContext("webgl2")가 null을 돌려줌' };
    const ext = typeof gl.getSupportedExtensions === 'function' ? gl.getSupportedExtensions() : null;
    if (!ext) return { ok: false, detail: 'WebGL2 확장 목록을 읽을 수 없음(컨텍스트 유실)' };
    try { gl.getExtension?.('WEBGL_lose_context')?.loseContext(); } catch { /* 무시 */ }
    return { ok: true };
  } catch (e) {
    return { ok: false, detail: msg(e) };
  }
}

export type StartupFailureCode = 'no-gpu' | 'gpu-lost' | 'boot';

/**
 * 호환 모드(WebGL2 우선) 판정. URL renderer=auto는 설정·세션 기록보다 우선한다(안내 화면의 '기본 모드로 다시 시도').
 * stored는 세션·설정을 읽는다. auto면 부르지 않으므로 저장소가 막혀도 기본 모드로 돌아간다.
 */
export function resolveCompatMode(urlRenderer: string | null, explicit: boolean | undefined, stored: () => boolean): boolean {
  if (urlRenderer === 'auto') return false;
  if (explicit || urlRenderer === 'webgl') return true;
  try { return stored(); } catch { return false; }
}

/** 시작을 막는 실패. 화면 안내는 code로 고르고 diagnostics는 접힌 기술 정보에만 쓴다. */
export class StartupError extends Error {
  constructor(readonly code: StartupFailureCode, readonly diagnostics: string[], cause?: unknown) {
    super(code + (diagnostics.length ? ': ' + diagnostics.join(' / ') : ''));
    this.name = 'StartupError';
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

/**
 * three 자체 대체(WebGPU 초기화 실패 → 같은 캔버스에 WebGL2)는 WebGL2 컨텍스트가 null인지 확인하지 않고
 * getSupportedExtensions를 부른다. 대체 직전에 WebGL2를 실제로 만들어 보고, 없으면 StartupError('no-gpu')로 바꾼다.
 * (게임 캔버스에는 이 시점 아직 컨텍스트가 없다. WebGPU 컨텍스트는 첫 출력 때 만들어진다.)
 */
export function guardWebGlFallback(renderer: unknown, probe: () => WebGl2ProbeResult): void {
  const internals = renderer as { _getFallback?: ((e: unknown) => unknown) | null };
  const threeFallback = internals._getFallback;
  if (typeof threeFallback !== 'function') return;
  internals._getFallback = (e: unknown) => {
    const gl = probe();
    if (!gl.ok) throw new StartupError('no-gpu', ['WebGPU 초기화 실패: ' + msg(e), describeWebGl2(gl)], e);
    return threeFallback(e);
  };
}

export interface BackendChoice {
  backend: 'webgpu' | 'webgl2';
  requiredLimits?: Record<string, number>;
  /** 왜 이 백엔드인지(진단용) */
  why: string;
  webgpu?: WebGpuProbeResult;
  webgl2?: WebGl2ProbeResult;
}

export function describeWebGpu(r: WebGpuProbeResult | undefined): string {
  if (!r) return 'WebGPU: 시험 안 함';
  return r.ok ? 'WebGPU: 사용 가능(' + r.ms + 'ms)' : 'WebGPU 실패[' + r.stage + ']: ' + (r.detail ?? '');
}
export function describeWebGl2(r: WebGl2ProbeResult | undefined): string {
  if (!r) return 'WebGL2: 시험 안 함';
  const n = r.attempts && r.attempts > 1 ? ' (' + r.attempts + '회 시도)' : '';
  return (r.ok ? 'WebGL2: 사용 가능' : 'WebGL2 실패: ' + (r.detail ?? '')) + n;
}

/** WebGPU 시험이 실제 GPU 자원을 건드린 뒤 실패한 단계(이때 GPU 프로세스가 다시 뜨는 동안 WebGL2가 잠깐 null일 수 있다). */
const TOUCHED_GPU: ReadonlySet<WebGpuStage | undefined> = new Set<WebGpuStage | undefined>(['device', 'buffers', 'pipeline', 'present', 'lost', 'validation', 'timeout']);
/** WebGL2 재시도 간격(ms). 합계 약 3.5초 뒤에도 null이면 GPU를 쓸 수 없는 상태로 본다. */
export const WEBGL2_RETRY_MS = [150, 300, 600, 1000, 1500] as const;

/** WebGL2 확인을 간격을 두고 다시 한다(성공하면 바로 멈춘다). */
export async function probeWebGl2Patient(probe: () => WebGl2ProbeResult, delays: readonly number[], sleep: (ms: number) => Promise<void> = delay): Promise<WebGl2ProbeResult> {
  let r = probe();
  let attempts = 1;
  for (const ms of delays) {
    if (r.ok) break;
    await sleep(ms);
    r = probe();
    attempts++;
  }
  return { ...r, attempts };
}

/**
 * 백엔드 선택. 기본은 WebGPU 시험 → 실패 시 WebGL2 확인. 호환 모드(forceWebGL)는 WebGL2를 먼저 보되,
 * WebGL2가 없으면 WebGPU를 시험한다(설정 하나로 시작 불가에 갇히지 않게). 둘 다 안 되면 StartupError('no-gpu').
 */
export async function chooseBackend(o: {
  forceWebGL: boolean;
  probeWebGpu: () => Promise<WebGpuProbeResult>;
  probeWebGl2: () => WebGl2ProbeResult;
  /** WebGPU가 GPU를 건드린 뒤 실패했을 때 WebGL2 재시도 간격(기본 WEBGL2_RETRY_MS) */
  retryDelaysMs?: readonly number[];
  sleep?: (ms: number) => Promise<void>;
}): Promise<BackendChoice> {
  if (o.forceWebGL) {
    const gl = o.probeWebGl2();
    if (gl.ok) return { backend: 'webgl2', why: 'compat-mode', webgl2: gl };
    const gp = await o.probeWebGpu();
    if (gp.ok) return { backend: 'webgpu', requiredLimits: gp.requiredLimits, why: 'compat-mode-but-no-webgl2', webgpu: gp, webgl2: gl };
    throw new StartupError('no-gpu', [describeWebGl2(gl), describeWebGpu(gp)]);
  }
  const gp = await o.probeWebGpu();
  if (gp.ok) return { backend: 'webgpu', requiredLimits: gp.requiredLimits, why: 'webgpu-ok', webgpu: gp };
  const gl = TOUCHED_GPU.has(gp.stage)
    ? await probeWebGl2Patient(o.probeWebGl2, o.retryDelaysMs ?? WEBGL2_RETRY_MS, o.sleep)
    : o.probeWebGl2();
  if (gl.ok) return { backend: 'webgl2', why: 'webgpu-failed:' + gp.stage, webgpu: gp, webgl2: gl };
  throw new StartupError('no-gpu', [describeWebGpu(gp), describeWebGl2(gl)]);
}

/** GPU 장치 유실 뒤 자동 재시작 기록. URL에도 남겨 저장소가 막혀도 재시작 횟수를 센다. */
export const RECOVER_PARAM = 'gpuretry';
export const RECOVER_STORE_KEY = 'dungeonhero.reloads';
export const MAX_GPU_RECOVERIES = 2;

export interface StorageLike { getItem(k: string): string | null; setItem(k: string, v: string): void }

export type RecoveryPlan = { reload: true; url: string; attempt: number } | { reload: false; attempts: number };

/**
 * 장치 유실 시 다음 행동. 횟수 = max(URL, 세션 저장소). 상한 미만이면 호환 모드(WebGL2) URL로 다시 열고,
 * 상한이면 자동 재시작을 멈춘다(안내 화면). 저장소 접근이 예외를 던져도 URL 횟수로 루프를 막는다.
 */
export function planGpuRecovery(href: string, storage: () => StorageLike | null, sessionRendererKey: string, max = MAX_GPU_RECOVERIES): RecoveryPlan {
  const url = new URL(href);
  const fromUrl = Number(url.searchParams.get(RECOVER_PARAM) ?? '0');
  let fromStore = 0;
  let store: StorageLike | null = null;
  try { store = storage(); fromStore = Number(store?.getItem(RECOVER_STORE_KEY) ?? '0'); } catch { store = null; }
  const n = Math.max(Number.isFinite(fromUrl) ? fromUrl : 0, Number.isFinite(fromStore) ? fromStore : 0);
  if (n >= max) return { reload: false, attempts: n };
  const attempt = n + 1;
  try {
    store?.setItem(RECOVER_STORE_KEY, String(attempt));
    store?.setItem(sessionRendererKey, 'webgl');
  } catch { /* 저장소 거부: URL 횟수만 쓴다 */ }
  url.searchParams.set('renderer', 'webgl');
  url.searchParams.set(RECOVER_PARAM, String(attempt));
  return { reload: true, url: url.toString(), attempt };
}
